<script setup lang="ts">
import { computed, ref } from "vue";
import { round } from "lodash-es";

// @ts-ignore we dont need types for this
import hsl2rgb from "hsl-rgb";
import FormSwitch from "@Forms/FormSwitch.vue";
import { VueECharts as VChart } from "@Helpers/echarts";
import useTranslation from "@Composables/useTranslation";
import { useHandleResize, ResizableChart } from "@Composables/useHandleResize";
import GeneralStats from "@Modules/analytics/DTOs/GeneralStats";
import { ValueChartDataProvider } from "@Modules/analytics/Components/PercentValueChart/ValueChart";
import ChartLayout from "@Modules/analytics/Components/ChartLayout.vue";

//
// Props
interface Props {
  dataset?: ValueChartDataProvider;
  title?: string;
  loading?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
    title: "Tree Chart",
    dataset: () => new GeneralStats(),
    loading: false,
});

//
// Setup
const { __ } = useTranslation();

//
// Data
const showPercent = ref<boolean>(false);
const chart = ref<ResizableChart | null>(null);

useHandleResize(chart);

//
// Computed
const chartData = computed(() => props.dataset.getChartData() || []);

//
// Methods

// @ts-ignore legacy chart code
const dec2hex = (n) => {
    n = parseInt(n);
    var c = "ABCDEF";
    var b: string | number = n / 16;
    var r = n % 16;
    b = b - r / 16;
    b = b >= 0 && b <= 9 ? b : c.charAt(b - 10);
    return r >= 0 && r <= 9 ? b + "" + r : b + "" + c.charAt(r - 10);
};

// @ts-ignore legacy chart code
const hsl2color = (h, s, l) => {
    const rgb = hsl2rgb(h, s, l);

    return (
        "#" + dec2hex(rgb[0]) + dec2hex(rgb[1]) + dec2hex(rgb[2])
    );
};

// @ts-ignore legacy chart code
const hsl2brightness = (h, s, l) => {
    const lightness = hsl2rgb(h, s, l);

    return (
        0.2126 * lightness[0] + 0.7152 * lightness[1] + 0.0722 * lightness[2]
    );
};

// @ts-ignore legacy chart code
const addColors = (data) => {
    const hueDelta = Math.floor(360 / data.length);
    let hue = 0;

    for (const i in data) {
        data[i].itemStyle = {
            color: hsl2color(hue, 0.5, 0.4),
            borderColor: hsl2color(hue, 0.5, 0.8),
        };

        if (data[i].children) {
            const lightDelta = 0.6 / data[i].children.length;
            let light = 0.4;

            for (const j in data[i].children) {
                const color = hsl2color(hue, 0.85, light);
                const brightness = hsl2brightness(hue, 0.85, light);

                data[i].children[j].itemStyle = {
                    color: color,
                    borderColor: color,
                };
                data[i].children[j].label = {
                    color: brightness > 120 ? "#000000" : "#FFFFFF",
                };

                light += lightDelta;
            }
        }

        hue += hueDelta;
    }

    return data;
};

const chartOptions = computed(() => {
    // @ts-ignore we need this unused value here so that this object re computes when we click the toggle
    // eslint-disable
    const shouldShowPercent = showPercent.value;
    return {
        title: {
            text: __("chart.title.devices"),
            left: "center",
            top: "top",
            show: false,
        },
        tooltip: {
            trigger: "item",
            formatter: "{b}: {c} ({d} %)",
        },
        toolbox: {
            show: false,
            feature: {
                saveAsImage: {
                    show: true,
                    title: __("chart.save_as_image"),
                },
            },
        },
        legend: {
            orient: "vertical",
            left: 10,
        },
        series: [
            {
                type: "pie",
                startAngle: 0,
                radius: ["20%", "80%"],
                center: ["50%", "50%"],
                clockwise: true,
                label: {
                    position: "inner",
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
                    formatter: (params: any) => {
                        if (showPercent.value) {
                            const percent = round((params.value / props.dataset.getChartTotal()) * 100, 0);
                            return `${percent}%`;
                        }
                        return `${params.value} click${chartData.value.length > 1 ? "s" : ""}`;
                    },
                    fontWeight: "bold",
                    fontSize: 16,
                    rotate: "radial",
                },
                itemStyle: {
                    color: "#ddd",
                    borderWidth: 0,
                },
                data: addColors(chartData.value || []),
            },
        ],
    };
});

</script>

<template>
  <ChartLayout
    class="pie-stat"
    :is-loading="loading"
  >
    <template #header>
      <div class="pie-stat__header">
        <span class="pie-stat__title">
          Top Devices
        </span>
        <FormSwitch
          v-model="showPercent"
          accessibility-label="Top Devices Toggler"
          input-id="switch-devices"
        />
      </div>
    </template>

    <v-chart
      ref="chart"
      class="pie-stat__chart"
      :options="chartOptions"
    />
  </ChartLayout>
</template>

<style lang="scss" scoped>
.pie-stat {
  display: flex;
  flex-flow: column;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__chart {
    flex: 1;
    width: 100%;
    min-width: 0;
  }
}
</style>
